import {
    AlertModal,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import dialogStyle from "./audit-log-dialog.scss";
import { DELETE_CONFIRMATION_TEXT } from "./AuditLogsTable";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { AuditLog } from "domain/auditLog";
import { AUTH_AUDIT_LOG_DELETE } from "domain/authority";
import { auditLogService, extractFilename } from "services/audit-log/AuditLogService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    auditLog: AuditLog;
    onDeleted?: () => void;
    onView?: () => void;
}

export function AuditLogKebabMenu(props: Props) {
    const { t } = useTranslation();
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState(false);
    const [errorModal, setErrorModal] = React.useState({ isOpen: false, title: "" });
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = React.useState(false);
    const theme = useTheme();
    const canDelete = !hasTenantCookie() && userSessionService.userHasAllAuthorities([AUTH_AUDIT_LOG_DELETE]);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    React.useEffect(() => {
        return () => abortControllers.forEach((controller) => controller.abort());
    }, []);

    const handleExport = () => {
        setIsExporting(true);
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.EXPORT_AUDIT_LOGS });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        auditLogService
            .exportAuditLogs({ uuids: [props.auditLog.uuid] }, abortController)
            .then((response) => {
                const filename = extractFilename(response.headers.get("Content-Disposition"));
                return response.blob().then((blob) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", filename);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("AuditLogs.export.exportSuccess", { count: 1 })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("Notification.title.auditLogExport.failed") });
            })
            .finally(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setIsExporting(false);
            });
    };

    const handleDelete = () => {
        setDeleteConfirmationVisible(false);
        setIsDeleting(true);
        usageStatisticsService.sendEvent({
            category: Category.AUDIT_LOGS,
            action: Action.DELETE_AUDIT_LOGS,
        });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        auditLogService
            .deleteAuditLogs({ uuids: [props.auditLog.uuid] }, abortController)
            .then((response) => {
                setIsDeleting(false);
                toast(
                    <SuccessIcon
                        successClass={layoutStyle.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={t("AuditLogs.deletion.deleteSuccess", { uuid: response.deletionReportUuid })}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layoutStyle.customToastSuccessMessage,
                    }
                );
                props.onDeleted?.();
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setIsDeleting(false);
                setErrorModal({ isOpen: true, title: t("Notification.title.auditLogDeletion.failed") });
            });
    };

    return (
        <>
            <DropdownMenu
                testId={testIds.common.primaryView.table.kebabMenu.itself}
                position={"bottom-start"}
                dropdownOffset={"Xxs"}
                closeOnSelection
                flipOptions={{ fallbackPlacements: ["top-start"] }}
            >
                <DropdownTrigger>
                    <IconButton>
                        <Kebab />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent>
                    <DropdownMenuItem
                        testId={testIds.workArea.auditLogs.logs.table.kebabMenu.viewButton}
                        onClick={props.onView}
                    >
                        {t("Common.view")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        testId={testIds.workArea.auditLogs.logs.table.kebabMenu.exportButton}
                        disabled={isExporting}
                        onClick={handleExport}
                    >
                        {t("Common.export")}
                    </DropdownMenuItem>
                    {canDelete && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                errorStyle
                                disabled={isDeleting}
                                onClick={() => setDeleteConfirmationVisible(true)}
                                testId={testIds.workArea.auditLogs.logs.table.kebabMenu.deleteButton}
                            >
                                {t("Common.delete")}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>
            <DeleteModal
                title={t("AuditLogs.deletion.title", { count: 1 })}
                isOpen={deleteConfirmationVisible}
                hideModal={() => setDeleteConfirmationVisible(false)}
                onDelete={handleDelete}
                className={dialogStyle.dialog}
                dataType="DELETE_AUDIT_LOG"
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_TEXT,
                    deleteButtonText: t("Common.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                testIds={{
                    itself: testIds.common.confirmationDialog.itself,
                    cancelButtonId: testIds.common.confirmationDialog.closeButton,
                    deleteButtonId: testIds.common.confirmationDialog.confirmButton,
                }}
            >
                {t("AuditLogs.deletion.confirmationMessage", { uuid: props.auditLog.uuid })}
            </DeleteModal>
            <AlertModal
                title={errorModal.title}
                variant="Warning"
                isOpen={errorModal.isOpen}
                hideModal={() => setErrorModal({ isOpen: false, title: "" })}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setErrorModal({ isOpen: false, title: "" })}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <Box marginTop="Sm">
                    {t("Common.genericErrorMessage")}
                    {t("Common.genericContactSupport")}
                </Box>
            </AlertModal>
        </>
    );
}

export default AuditLogKebabMenu;
